<template>
  <div>
    <PageHeader :title="title" :items="items"/>
    <b-card no-body class="table-responsive">
      <b-card-body>
        <div class="col-sm-12">
          <div class="row">
            <div class="col-sm-6 col-12">
              <PerPageSelector v-model="query.perPage"
                               :available-per-page-options="availablePerPageOptions"
              ></PerPageSelector>
            </div>
            <div class="col-sm-3 offset-lg-3 offset-md-3 offset-sm-3 col-12">
              <DataTableSearch v-model="query.filter"
                               :place-holder="$t('pages.product_research.show.search')">
              </DataTableSearch>
            </div>
          </div>
        </div>
        <b-table
            class="table-centered align-middle"
            ref="researchItemTable"
            striped
            hover
            :empty-text="$t('components.bootstrap_table.empty_text')"
            :empty-filtered-text="$t('components.bootstrap_table.empty_filtered_text')"
            show-empty
            :fields="fields"
            :items="getMatches"
            :stacked="$store.state.app.isMobile"
            :filter="query.filter"
            :per-page="query.perPage"
            :sort-by="query.sortBy"
            :sort-desc="query.sortDesc"
            :current-page="query.page"
        >
          <template #cell(thumbnail)="data">
            <div class="list-group list-group-flush">
              <b-row class="media align-items-center g-0">
                <b-col sm="4">
                  <b-avatar square size="9em" :src="data.item.thumbnail"></b-avatar>
                </b-col>
                <b-col sm="8">
                  <div class="media-body overflow-hidden">
                    <h5 class="font-size-13">
                      {{ data.item.title }}
                    </h5>
                    <a class="mb-0 text-truncate"
                       @click="openAmazonPage(data.item.url)"
                       href="javascript: void(0);">
                      <i class="mdi mdi-open-in-new"></i>
                      {{ data.item.asin }}
                    </a>
                  </div>
                </b-col>
              </b-row>
            </div>
          </template>
          <template #cell(tags)="data">
            <h6 v-if="data.item.amazon_choice">
              <span class="badge badge-soft-warning py-1">
                {{ $t('pages.product_research.show.amazon_choice') }}
              </span>
            </h6>
            <h6 v-if="data.item.best_seller">
                <span class="badge badge-soft-success py-1">
                {{ $t('pages.product_research.show.best_seller') }}
              </span>
            </h6>
            <h6 v-if="data.item.amazon_prime">
               <span class="badge badge-soft-primary py-1">
                {{ $t('pages.product_research.show.amazon_prime') }}
              </span>
            </h6>
            <h6 v-if="data.item.sponsored">
              <span class="badge badge-soft-secondary py-1">
               {{ $t('pages.product_research.show.sponsored') }}
              </span>
            </h6>
          </template>
          <template #cell(position)="data">
            <ul class="list-inline d-flex">
              <li
                  v-b-tooltip.hover.top
                  class="list-inline-item me-3"
                  :title="$t('pages.product_research.show.positions.global')"
              >
                <i class="mdi mdi-earth me-1"></i>
                {{ data.item.position.global_position }}
              </li>
              <li
                  v-b-tooltip.hover.top
                  class="list-inline-item me-3"
                  :title="$t('pages.product_research.show.positions.page')"
              >
                <i class="mdi mdi-book-open-page-variant me-1"></i>
                {{ data.item.position.page }}
              </li>
              <li
                  v-b-tooltip.hover.top
                  class="list-inline-item me-3"
                  :title="$t('pages.product_research.show.positions.order')"
              >
                <i class="mdi mdi-reorder-horizontal me-1"></i>
                {{ data.item.position.position }}
              </li>
            </ul>
          </template>
          <template #cell(price)="data">
            <p>{{ data.item.price.current_price }} {{ data.item.price.currency }}</p>
          </template>
          <template #cell(reviews.total_reviews)="data">
            <p>{{ data.item.reviews.total_reviews }}</p>
          </template>

          <template #cell(reviews.rating)="data">
            <star :star="data.item.reviews.rating" class="d-flex mb-3"></star>
          </template>

          <template #cell(similarity)="data">
            <h5>
              <span class="badge badge-soft-secondary mb-2">
                %{{ data.item.similarity | toPercentage(0) }}
              </span>
            </h5>
          </template>
          <template #cell(score)="data">
            <p>{{ data.item.score }}</p>
          </template>
        </b-table>
        <div class="row">
          <div class="col-sm">
            <Pagination v-model="query.page" :meta="matches.meta"></Pagination>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import queryBuilder from "../../../../mixins/queryBuilder";
import ProductResearchService from "../../../../Services/Api/ProductResearchService";
import Star from "../../../Reviews/components/Star";
import PerPageSelector from "../../../../components/PerPageSelector";

export default {
  components: {PerPageSelector, Star},
  page() {
    return {
      title: this.$t('pages.product_research.title'),
      meta: [
        {
          name: "description",
          content: this.$t('common.app_title')
        }
      ]
    }
  },
  name: "ProductResearchShow",
  mixins: [queryBuilder],
  data() {
    return {
      title: this.$t('pages.product_research.show.title'),
      items: [
        {
          text: this.$t('pages.product_research.show.items.first'),
          href: "/",
        },
        {
          text: this.$t('pages.product_research.show.items.second'),
          active: true,
        },
      ],
      fields: [
        {
          key: 'thumbnail',
          sortable: false,
          tdClass: 'thumbnail',
          label: this.$t('pages.product_research.show.fields.product')
        },
        {key: 'reviews.rating', sortable: false, label: this.$t('pages.product_research.show.fields.rating')},
        {key: 'position', sortable: false, label: this.$t('pages.product_research.show.fields.rank')},
        {
          key: 'reviews.total_reviews',
          sortable: false,
          label: this.$t('pages.product_research.show.fields.total_reviews')
        },
        {key: 'price', sortable: false, label: this.$t('pages.product_research.show.fields.price')},
        {key: 'score', sortable: false, label: this.$t('pages.product_research.show.fields.score')},
        {key: 'similarity', sortable: true, label: this.$t('pages.product_research.show.fields.similarity')},
        {key: 'tags', sortable: false, label: this.$t('pages.product_research.show.fields.tags')},
      ],
      matches: {},
      matchId: ''
    };
  },
  methods: {
    async getMatches(params = this.query) {
      this.query = {...this.query, ...params}
      const query = this.buildQuery()
      const {data: matches} = await ProductResearchService.show({id: this.matchId, query})
      this.matches = matches
      return matches.data
    },
    openAmazonPage(url) {
      window.open(url, '_blank')
    },
  },
  created() {
    this.matchId = this.$route.params?.matchId
  },
};
</script>
<style scoped>
/deep/ .thumbnail {
  margin-left: auto;
  margin-right: auto;
  max-width: 340px;
}
@media (max-width: 920px) {
  /deep/ .thumbnail {
    max-width: 100%;
  }
}
</style>